import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Autocomplete, Checkbox, FormControlLabel,
  Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, IconButton, MenuItem, Typography,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createShipment, listPickingLists, type SaveShipmentPackagePayload, type PackageType } from '../../api/logistica'
import { errorMessage } from '../../api/client'

export default function CreateShipmentDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient()
  const [pickingListId, setPickingListId] = useState<string | null>(null)
  const [carrierName, setCarrierName] = useState('')
  const [vehiclePlate, setVehiclePlate] = useState('')
  const [isOwnFleet, setIsOwnFleet] = useState(false)
  const [isInternational, setIsInternational] = useState(false)
  const [transportCost, setTransportCost] = useState('')
  const [packages, setPackages] = useState<SaveShipmentPackagePayload[]>([])

  const { data: pickingLists } = useQuery({ queryKey: ['logistica', 'picking'], queryFn: listPickingLists, enabled: open })
  const confirmed = pickingLists?.filter((p) => p.status === 'Confirmata') ?? []

  const reset = () => {
    setPickingListId(null); setCarrierName(''); setVehiclePlate(''); setIsOwnFleet(false); setIsInternational(false)
    setTransportCost(''); setPackages([])
  }
  const handleClose = () => { reset(); mutation.reset(); onClose() }

  const addPackage = () => setPackages((p) => [...p, { packageNumber: p.length + 1, type: 'Cutie', weightKg: 0, contentsDescription: '' }])
  const removePackage = (idx: number) => setPackages((p) => p.filter((_, i) => i !== idx).map((pkg, i) => ({ ...pkg, packageNumber: i + 1 })))
  const updatePackage = (idx: number, patch: Partial<SaveShipmentPackagePayload>) =>
    setPackages((p) => p.map((pkg, i) => (i === idx ? { ...pkg, ...patch } : pkg)))

  const mutation = useMutation({
    mutationFn: () => createShipment({
      pickingListId: pickingListId!, carrierName, vehiclePlate: vehiclePlate || null, isOwnFleet, isInternational,
      transportCost: transportCost ? Number(transportCost) : null, packages,
    }),
    onSuccess: ({ id }) => {
      void qc.invalidateQueries({ queryKey: ['logistica', 'expeditii'] })
      onCreated(id)
    },
  })

  const canSubmit = !!pickingListId && !!carrierName

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Expediere nouă</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Autocomplete
            options={confirmed.map((p) => p.id)}
            getOptionLabel={(id) => { const p = confirmed.find((x) => x.id === id); return p ? `${p.number} — ${p.orderNumber} (${p.clientName})` : id }}
            value={pickingListId} onChange={(_, v) => setPickingListId(v)}
            renderInput={(params) => <TextField {...params} label="Listă de picking confirmată" />}
          />
          <Stack direction="row" spacing={2}>
            <TextField label="Transportator" value={carrierName} onChange={(e) => setCarrierName(e.target.value)} sx={{ flexGrow: 1 }} />
            <TextField label="Nr. înmatriculare (opțional)" value={vehiclePlate} onChange={(e) => setVehiclePlate(e.target.value)} sx={{ width: 200 }} />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <FormControlLabel control={<Checkbox checked={isOwnFleet} onChange={(e) => setIsOwnFleet(e.target.checked)} />} label="Flotă proprie" />
            <FormControlLabel control={<Checkbox checked={isInternational} onChange={(e) => setIsInternational(e.target.checked)} />} label="Transport internațional (CMR)" />
            <TextField type="number" label="Cost transport (opțional)" value={transportCost} onChange={(e) => setTransportCost(e.target.value)} sx={{ width: 200 }} />
          </Stack>

          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2">Pachete (opțional)</Typography>
            <Button size="small" startIcon={<Add />} onClick={addPackage}>Adaugă pachet</Button>
          </Stack>
          {packages.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>#</TableCell><TableCell>Tip</TableCell><TableCell>Greutate (kg)</TableCell><TableCell>Conținut</TableCell><TableCell /></TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{pkg.packageNumber}</TableCell>
                    <TableCell>
                      <TextField select size="small" value={pkg.type} onChange={(e) => updatePackage(idx, { type: e.target.value as PackageType })}>
                        <MenuItem value="Cutie">Cutie</MenuItem>
                        <MenuItem value="Palet">Palet</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField type="number" size="small" value={pkg.weightKg} onChange={(e) => updatePackage(idx, { weightKg: Number(e.target.value) })} sx={{ width: 100 }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={pkg.contentsDescription ?? ''} onChange={(e) => updatePackage(idx, { contentsDescription: e.target.value })} fullWidth />
                    </TableCell>
                    <TableCell><IconButton size="small" onClick={() => removePackage(idx)}><Delete fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose}>Renunță</Button>
        <Button variant="contained" disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Creează
        </Button>
      </DialogActions>
    </Dialog>
  )
}
